let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const C = window.AudioContext || (window as any).webkitAudioContext;
    if (!C) return null;
    ctx = new C();
  }
  if (ctx.state === "suspended") ctx.resume().catch(() => undefined);
  return ctx;
}

interface ToneOpts { type?: OscillatorType; gain?: number; attack?: number; slideTo?: number; lowpass?: number }

function tone(freq: number, at: number, dur: number, opts: ToneOpts = {}) {
  const a = audio();
  if (!a) return;
  const { type = "square", gain = 0.06, attack = 0.005, slideTo, lowpass } = opts;
  const t0 = a.currentTime + at;
  const osc = a.createOscillator();
  const g = a.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (slideTo) osc.frequency.exponentialRampToValueAtTime(slideTo, t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  let node: AudioNode = osc;
  if (lowpass) {
    const f = a.createBiquadFilter();
    f.type = "lowpass";
    f.frequency.setValueAtTime(lowpass, t0);
    node = osc.connect(f);
  }
  node.connect(g).connect(a.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

export type SoundStyle = "gameshow" | "arcade" | "orchestra";

export const SOUND_STYLES: { id: SoundStyle; label: string; blurb: string }[] = [
  { id: "gameshow", label: "Game show", blurb: "Drumroll, clicks & a big brassy ta-da" },
  { id: "arcade", label: "Arcade", blurb: "8-bit blips and a chiptune level-up" },
  { id: "orchestra", label: "Orchestra", blurb: "Timpani roll and a triumphant chord" },
];

const chord = (notes: number[], at: number, dur: number, opts: ToneOpts) => notes.forEach((f) => tone(f, at, dur, opts));

const STYLES: Record<SoundStyle, {
  tick: (urgent: boolean) => void; buzzer: () => void; click: (i: number, at: number) => void; roll: (t: number, p: number) => void; rollStep: number; fanfare: () => void;
}> = {
  gameshow: {
    tick: (u) => tone(u ? 1500 : 950, 0, 0.06, { gain: u ? 0.08 : 0.05 }),
    buzzer: () => { tone(196, 0, 0.45, { type: "sawtooth", gain: 0.07 }); tone(147, 0.05, 0.45, { type: "sawtooth", gain: 0.05 }); },
    click: (i, at) => tone(1200 - i * 12, at, 0.03, { gain: 0.035 }),
    roll: (t, p) => tone(90 + Math.random() * 30, t, 0.04, { type: "triangle", gain: 0.05 + p * 0.05 }),
    rollStep: 0.045,
    fanfare: () => {
      [523, 659, 784].forEach((f, i) => tone(f, i * 0.11, 0.16, { type: "triangle", gain: 0.09 }));
      chord([1047, 1319, 1568], 0.36, 0.7, { type: "triangle", gain: 0.06 });
      chord([523, 784], 0.36, 0.7, { type: "sawtooth", gain: 0.025 });
    },
  },
  arcade: {
    tick: (u) => tone(u ? 1760 : 1320, 0, 0.05, { gain: u ? 0.06 : 0.04, slideTo: u ? 2200 : 1500 }),
    buzzer: () => [392, 370, 349, 262].forEach((f, i) => tone(f, i * 0.16, i === 3 ? 0.5 : 0.15, { gain: 0.06, slideTo: f * 0.94 })),
    click: (i, at) => tone(660 + (i % 4) * 220, at, 0.025, { gain: 0.03 }),
    roll: (t, p) => tone(220 + p * 660, t, 0.03, { gain: 0.025, type: "square" }),
    rollStep: 0.06,
    fanfare: () => {
      [523, 659, 784, 1047, 784, 1047, 1319, 1568].forEach((f, i) => tone(f, i * 0.065, 0.07, { gain: 0.05 }));
      tone(2093, 0.56, 0.45, { gain: 0.05, slideTo: 2637 });
      tone(131, 0.56, 0.45, { type: "triangle", gain: 0.08 });
    },
  },
  orchestra: {
    tick: (u) => tone(u ? 1046 : 784, 0, 0.09, { type: "sine", gain: u ? 0.12 : 0.08 }),
    buzzer: () => chord([147, 175, 220], 0, 1.1, { type: "sawtooth", gain: 0.035, attack: 0.08, lowpass: 900, slideTo: 130 }),
    click: (i, at) => tone(1568 - i * 20, at, 0.05, { type: "sine", gain: 0.04 }),
    roll: (t, p) => tone(65 + Math.random() * 8, t, 0.09, { type: "sine", gain: 0.06 + p * 0.1 }),
    rollStep: 0.05,
    fanfare: () => {
      chord([196, 247, 294], 0, 0.3, { type: "sawtooth", gain: 0.035, attack: 0.03, lowpass: 1600 });
      chord([262, 330, 392, 523], 0.32, 1.4, { type: "sawtooth", gain: 0.035, attack: 0.05, lowpass: 2200 });
      chord([1047, 1319], 0.32, 1.4, { type: "sine", gain: 0.04, attack: 0.05 });
      tone(65, 0.32, 0.8, { type: "sine", gain: 0.18 });
    },
  },
};

const pick = (style?: string) => STYLES[(style as SoundStyle)] || STYLES.gameshow;

export const sfx = {
  unlock: () => { audio(); },
  stopAll: () => {
    ctx?.close().catch(() => undefined);
    ctx = null;
  },
  tick: (urgent = false, style?: string) => pick(style).tick(urgent),
  buzzer: (style?: string) => pick(style).buzzer(),
  spin: (seconds: number, clicks = 28, style?: string) => {
    const s = pick(style);
    for (let i = 0; i < clicks; i++) s.click(i, seconds * (i / clicks) ** 1.9);
  },
  drumroll: (seconds: number, style?: string) => {
    const s = pick(style);
    for (let t = 0; t < seconds; t += s.rollStep) s.roll(t, t / seconds);
  },
  fanfare: (style?: string) => pick(style).fanfare(),
};
